// docs/33 E2 — the coach line along the top of the board: what a click will do right now, in the game's own
// words (the authoring machine's suggestion or the chosen action's verdict), and the keys that
// matter in this state. It never decides anything; it reads the AuthoringStore.
import { Kbd } from '../components';
import { hotbarLabel, keyLabel, panLabel, useHotkeyOverrides } from '../../core/input/hotkeys';
import { useAuthoring } from '../../core/stores/authoring';
import { useEncounter } from '../../core/encounter/session';
import { usePlayback } from '../../core/stores/playback';
import { useCinematic } from '../../core/stores/cinematic';
import { useView } from '../../core/stores/view';
import { reasonText } from '../../core/authoring/stateMachine';
import { SLOTS } from '../../core/sim/types';
import { tileEq } from '../../core/sim/types';
import { consumable } from '../../core/sim/items';

type Tone = 'move' | 'attack' | 'support' | 'blocked' | 'plain';

export function Coach() {
  const st = useAuthoring((s) => s.machineState);
  // docs/87: the keys named here are the player's own
  const o = useHotkeyOverrides();
  const tab = keyLabel('board.nextHero', o), ready = keyLabel('board.ready', o), bar = hotbarLabel(o);
  const sug = useAuthoring((s) => s.suggestion);
  const probe = useAuthoring((s) => s.probe);
  const cursor = useAuthoring((s) => s.cursor);
  const local = useAuthoring((s) => s.local);
  const rows = useAuthoring((s) => s.rows);
  const playing = usePlayback((s) => s.playing);
  // docs/56 EP5: the defeat card has the top of the board
  const lost = useCinematic((s) => !!s.defeat && !s.defeat.continued && s.defeat.encounterId === useEncounter.getState().encounterId);
  const aimTargets = useView((s) => s.aimTargets);
  const name = useEncounter((s) => (st.kind === 'Idle' ? undefined : s.snapshot?.units.find((u) => u.heroId === st.heroId)?.name));
  const itemDef = useEncounter((s) => (st.kind === 'ActionChosen' && st.pending.item !== undefined ? s.snapshot?.bags?.flatMap((b) => b.items).find((i) => i.item === st.pending.item)?.def : undefined));
  const itemName = itemDef !== undefined ? consumable(itemDef)?.name : undefined;
  if (playing || lost) return null;
  let tone: Tone = 'plain', line: string, about: string | undefined, keys: Array<[string, string]>;
  if (st.kind === 'Idle') {
    line = tab ? `Pick a hero to plan: click it, or press ${tab}` : 'Pick a hero to plan: click it';
    keys = [[tab, 'next hero'], [panLabel(o), 'pan'], [keyLabel('board.ping', o), 'ping']];
  } else if (st.kind === 'ActionChosen') {
    const bad = probe && !probe.verdict.legal && tileEq(probe.tile, cursor) ? probe.verdict : undefined;
    tone = bad ? 'blocked' : st.action.glyph === 'move' ? 'move' : st.action.glyph === 'attack' || st.action.glyph === 'beam' || st.action.glyph === 'area' ? 'attack' : 'support';
    // docs/40: Use Item names the item it is aiming
    const what = st.pending.item !== undefined ? `${st.action.name} (${itemName ?? 'item'})` : st.action.name;
    // docs/48 AR5: how many the range reaches from where the hero will stand
    const reach = aimTargets === undefined ? '' : aimTargets === 0 ? ' · nothing in reach from there' : ` · ${aimTargets} in reach`;
    line = bad ? `${what}: ${reasonText(bad.reason)}` : `${what} · slot ${st.slot} — ${AIM[st.action.args] ?? 'click to aim'}${st.action.args === 'item' && keyLabel('board.confirm', o) ? ` (${keyLabel('board.confirm', o)}: yourself)` : ''}${reach}`;
    keys = [['Click', 'aim'], [keyLabel('board.back', o), 'back'], ['Right-click', 'deselect']];
  } else {
    const heroId = st.heroId;
    const has = (s: (typeof SLOTS)[number]) => local.has(`${heroId}:${s}`) || rows.has(`${heroId}:${s}`);
    const committed = [...rows.values()].some((r) => r.heroId === heroId && r.state === 'committed');
    if (committed && sug?.kind !== 'select') { line = `Your plan is locked — Unlock plan${ready ? ` (${ready})` : ''} to change it`; keys = [[ready, 'unlock'], [tab, 'next hero']]; }
    else if (sug) {
      tone = sug.kind === 'blocked' ? 'blocked' : sug.kind === 'select' ? 'plain' : sug.tone;
      line = sug.kind === 'plan' && sug.hit ? `${sug.label} — ${sug.hit.damage} damage, HP ${sug.hit.hpStart} → ${Math.max(0, sug.hit.hpEnd)}${sug.hit.drops ? ', it drops' : ''}` : sug.label;
      if (sug.kind === 'plan') about = sug.about;
      keys = sug.kind === 'blocked' ? [[bar, 'actions'], [ready, 'lock plan']] : [['Click', sug.kind === 'select' ? 'select' : 'plan it'], [bar, 'actions'], [ready, 'lock plan']];
    } else if (has(1) && has(2)) { line = `${name ?? 'This hero'}'s actions are planned — ${ready || 'Lock plan'} locks your plan; Undo clears the last`; keys = [[ready, 'lock plan'], [tab, 'next hero']]; }
    else {
      line = st.kind === 'SlotChosen' ? `Slot ${st.slot}: pick an action, or click the board` : `${name ?? 'Hero'}: click open ground to move, a foe to attack`;
      keys = [[bar, 'actions'], [ready, 'lock plan'], ['Right-click', 'deselect']];
    }
  }
  return (
    <div className={`fk-hud__coach fk-hud__coach--${tone}`} role="status" aria-live="polite" data-testid="coach" data-tone={tone}>
      <span className="fk-hud__coach-line">{line}{about && <span className="fk-hud__coach-about">{about}</span>}</span>
      <span className="fk-hud__coach-keys" aria-hidden="true">{keys.filter(([k]) => k).map(([k, v]) => <span key={v}><Kbd>{k}</Kbd> {v}</span>)}</span>
    </div>
  );
}

const AIM: Record<string, string> = {
  tile: 'click a square', unit: 'click a target', unitDir: 'click a target, then a direction', direction: 'click a direction',
  line: 'click where the line starts', interact: 'click a stash or a mechanism', none: 'placed at once',
  item: 'click yourself or a friend beside you',
};
